import { useEffect, useRef } from 'preact/hooks';
import { Disclaimer } from './Disclaimer';
import { EscalationForm } from './EscalationForm';
import { HandoffPrompt } from './HandoffPrompt';
import { Message } from './Message';
import { MessageInput } from './MessageInput';
import { SuggestedQuestions } from './SuggestedQuestions';
import { CloseIcon, LogoMark } from './icons';
import type { LivePhase } from '../App';

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  id?: string;
}

export type EscalationState =
  | { kind: 'hidden' }
  | { kind: 'form'; busy: boolean; error?: string }
  | { kind: 'done'; email: string; ticketCode?: string };

export type HandoffState =
  | { kind: 'hidden' }
  | { kind: 'prompt'; summary?: string; busy: boolean; error?: boolean }
  | { kind: 'ticket'; email: string; ticketCode?: string };

export function ChatPanel({
  brandColor,
  disclaimer,
  showDisclaimer,
  greeting,
  suggestedQuestions,
  messages,
  busy,
  terminal,
  escalation,
  handoff,
  liveAvailable,
  livePhase,
  liveAgentName,
  liveEndedReason,
  liveTicketCode,
  onLeaveMessage,
  onClose,
  onSend,
  onEscalateSubmit,
  onHandoffSubmit,
  onChatAgain,
  onCreateTicket,
  onRate
}: {
  brandColor: string;
  disclaimer: string;
  showDisclaimer: boolean;
  greeting: string;
  suggestedQuestions: string[];
  messages: Msg[];
  busy: boolean;
  terminal: boolean;
  escalation: EscalationState;
  handoff: HandoffState;
  liveAvailable: boolean;
  livePhase: LivePhase;
  liveAgentName: string | null;
  liveEndedReason: string | null;
  liveTicketCode: string | null;
  onLeaveMessage: () => void;
  onClose: () => void;
  onSend: (text: string) => void;
  onEscalateSubmit: (data: {
    name: string;
    email: string;
    phone: string;
    message: string;
  }) => void;
  onHandoffSubmit: (email: string) => void;
  onChatAgain: () => void;
  onCreateTicket: () => void;
  onRate: (messageId: string, rating: 1 | -1) => Promise<boolean>;
}) {
  // Header identity reflects the real state: the AI mark normally, the agent's
  // initial once a human is on the chat; a presence dot that turns amber while
  // connecting; a subtitle that announces agent availability.
  const isActive = livePhase === 'active';
  const isConnecting = livePhase === 'connecting';
  const agentInitial = liveAgentName?.trim().charAt(0).toUpperCase() ?? '';
  const subtitle = isActive
    ? liveAgentName
      ? `${liveAgentName} · Live`
      : 'Live chat'
    : isConnecting
      ? 'Connecting you to an agent…'
      : liveAvailable
        ? 'AI assistant · Agents online'
        : 'AI assistant';

  // Last rendered turn — used to suppress the typing indicator once the
  // assistant's reply has begun streaming text (so there's never a stray
  // empty bubble next to the dots).
  const lastMsg = messages[messages.length - 1];

  // Keep the thread pinned to the latest message as turns arrive / stream.
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, livePhase, handoff]);

  return (
    <div class="panel" style={{ ['--brand' as string]: brandColor }}>
      <div class="panel-header">
        <div class="avatar">
          {isActive && agentInitial ? agentInitial : <LogoMark />}
          <span class={`presence-dot${isConnecting ? ' busy' : ''}`} />
        </div>
        <div class="header-id">
          <div class="header-name">Stratus Financial</div>
          <div class="header-sub">{subtitle}</div>
        </div>
        <button class="header-close" onClick={onClose} aria-label="Close chat">
          <CloseIcon />
        </button>
      </div>

      {/* The "automated assistant, not financial advice" notice. Required for
          a first-time visitor (spec Phase 1); it dismisses itself once the
          conversation starts so it does not crowd the thread. */}
      {showDisclaimer && disclaimer && <Disclaimer text={disclaimer} />}

      <div class="thread" ref={threadRef}>
        {messages.length === 0 && escalation.kind === 'hidden' && handoff.kind === 'hidden' && (
          <>
            <Message role="assistant" content={greeting} showAvatar />
            <SuggestedQuestions questions={suggestedQuestions} onPick={onSend} />
          </>
        )}
        {messages.map((m, i) => {
          // Skip the empty placeholder bubble while the assistant turn streams —
          // the typing indicator below is the single placeholder until text arrives.
          if (m.role === 'assistant' && m.content === '') return null;
          const prev = messages[i - 1];
          const showAvatar = m.role === 'assistant' && (!prev || prev.role !== 'assistant');
          const grouped = Boolean(prev) && prev.role === m.role;
          return (
            <Message
              key={i}
              role={m.role}
              content={m.content}
              showAvatar={showAvatar}
              grouped={grouped}
              // Only bot turns from this session carry an id, and only while
              // the conversation is still a bot conversation — rating a live
              // agent's message is not a thing we ask for.
              messageId={livePhase === 'off' ? m.id : undefined}
              onRate={onRate}
            />
          );
        })}
        {busy && !(lastMsg && lastMsg.role === 'assistant' && lastMsg.content !== '') && (
          <div class="msg-row assistant">
            <div class="msg-avatar" aria-hidden="true">
              <LogoMark />
            </div>
            <div class="bubble assistant" aria-label="Assistant is typing">
              <span class="typing">
                <span />
                <span />
                <span />
              </span>
            </div>
          </div>
        )}
        {terminal && escalation.kind === 'hidden' && handoff.kind === 'hidden' && livePhase === 'off' && (
          <Message
            role="assistant"
            content="This conversation has ended. Please reload the page to start a new one."
          />
        )}
        {escalation.kind === 'form' && escalation.error && (
          <Message
            role="assistant"
            content={
              escalation.error === 'rate_limited'
                ? 'Too many requests. Please try again in a few minutes.'
                : escalation.error === 'already_escalated'
                  ? 'A message has already been sent for this conversation.'
                  : 'Something went wrong sending your message. Please try again.'
            }
          />
        )}
      </div>

      {handoff.kind === 'prompt' && (
        <HandoffPrompt
          busy={handoff.busy}
          error={handoff.error}
          available={liveAvailable}
          onSubmit={onHandoffSubmit}
        />
      )}
      {handoff.kind === 'ticket' && (
        <div class="escalation-done">
          Thanks — someone from our team will reply to <strong>{handoff.email}</strong> shortly.
          {handoff.ticketCode && (
            <span> Your ticket number is <strong>{handoff.ticketCode}</strong>.</span>
          )}
        </div>
      )}
      {escalation.kind === 'form' && (
        <EscalationForm
          busy={escalation.busy}
          onSubmit={onEscalateSubmit}
          onChatAgain={onChatAgain}
        />
      )}
      {escalation.kind === 'done' && (
        <div class="escalation-done">
          Thanks — someone from our team will reply to <strong>{escalation.email}</strong> within
          one business day.
          {escalation.ticketCode && (
            <span> Your ticket number is <strong>{escalation.ticketCode}</strong>.</span>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button type="button" class="live-secondary" onClick={onChatAgain}>
              Chat again
            </button>
          </div>
        </div>
      )}
      {livePhase === 'connecting' && (
        <div class="live-status">
          <span class="live-spinner" aria-hidden="true" />
          Waiting for an agent…
        </div>
      )}
      {livePhase === 'connecting' && escalation.kind === 'hidden' && (
        <button class="live-secondary" onClick={onLeaveMessage}>
          Create a ticket instead
        </button>
      )}
      {livePhase === 'ended' && escalation.kind === 'hidden' && (
        <div class="escalation-done">
          {liveEndedReason === 'visitor_inactive'
            ? 'This chat was closed after a period of inactivity.'
            : 'This chat has ended.'}
          {liveTicketCode && (
            <span> Your ticket number is <strong>{liveTicketCode}</strong>.</span>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button type="button" class="live-secondary" onClick={onChatAgain}>
              Chat again
            </button>
            {!liveTicketCode && (
              <button type="button" class="live-secondary" onClick={onCreateTicket}>
                Create a ticket
              </button>
            )}
          </div>
        </div>
      )}

      {/* Input is shown when no escalation form/done or handoff prompt is up.
          During connecting (queued) and active live sessions it stays enabled —
          so the visitor can type their concern while waiting — even if the (bot)
          conversation went terminal. */}
      {escalation.kind === 'hidden' && handoff.kind === 'hidden' && livePhase !== 'ended' && (
        <MessageInput
          disabled={
            livePhase === 'connecting' || livePhase === 'active' ? false : busy || terminal
          }
          onSend={onSend}
        />
      )}
    </div>
  );
}
